import React from 'react';
import { Box, Button, Form, Heading } from '@zillow/constellation-10';
import { Form as ReactFinalForm } from 'react-final-form';
import KiqQuestion from './KiqQuestion';
import {
  useId,
  useIdVerificationCallbacks,
  useKiqQuestionSet,
  usePidAnswers,
  useSetLoading,
  useSetPidRemainingAttempts,
  useTermSet,
} from '../../contexts/IdVerificationContext';
import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import { useIdVerificationWebApiHandler_submitIdentityCheckAnswers } from '@zg-rentals/ts-id-verification-client-react-query';
import { getWizardOnSubmit } from '../Content.utils';
import type { ButtonProps } from '../../NextButton/NextButton';
import PrivacyCard from '../../PrivacyCard';
import { getZgAuthZoneFromTermSet } from '../../utils/zgAuthZoneUtils';
import { ExitReason, IdvStep, LogLevel, TermSet } from '../../../../models';
import { LANDLORD_CLICKSTREAM_EVENTS, RENTER_CLICKSTREAM_EVENTS } from '../../clickstream/events';
import { ErrorCodes } from '../../constants/errorCodes';
import { GENERIC_ERROR_SUBSTEP, INCORRECT_ANSWERS_SUBSTEP, USER_LOCKED_OUT_SUBSTEP } from '../../constants/steps';
import { DatarouterError } from '@zg-rentals/web-base';
import { LANDLORD_METRIC_PREFIX, RENTER_METRIC_PREFIX } from '../../IdVerification';

// Mutation key for submitIdentityCheckAnswers
export const submitIdentityAnswersMutationKey = ['IdVerificationWebApiHandler_submitIdentityCheckAnswers'];

interface KiqStepProps {
  id: string;
  label: string;
}

export default function KiqStep({ id, label }: KiqStepProps) {
  const kiqQuestions = useKiqQuestionSet();
  const { onRequestNext, onRequestStep } = useWizard();
  const requestingClient = useId();
  const pidAnswers = usePidAnswers();
  const termSet = useTermSet();
  const ssnProvided = Boolean(pidAnswers.ssn);
  const setLoading = useSetLoading();
  const setPidRemainingAttempts = useSetPidRemainingAttempts();
  const { onExit, trackEvent, emitMetric, log } = useIdVerificationCallbacks();

  const { mutateAsync } = useIdVerificationWebApiHandler_submitIdentityCheckAnswers({
    mutationKey: submitIdentityAnswersMutationKey,
    onMutate: async () => {
      setLoading(true);
      if (termSet === TermSet.LandlordScreening) {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNextSubmitKBA);
      } else {
        trackEvent(RENTER_CLICKSTREAM_EVENTS.submitKba);
      }
    },
    onSuccess: async () => {
      // Success means answers were correct and user is verified
      if (termSet === TermSet.LandlordScreening) {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.idvSuccess);
        emitMetric(`${LANDLORD_METRIC_PREFIX} submitIdentityAnswers success`);
      } else {
        emitMetric(`${RENTER_METRIC_PREFIX} submitIdentityAnswers success`);
      }
      await onExit({ reason: ExitReason.IdVerified, success: true });
    },
    onError(error) {
      if (termSet === TermSet.LandlordScreening) {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.idvSuccess);
        emitMetric(`${LANDLORD_METRIC_PREFIX} submitIdentityAnswers error`);
      } else {
        emitMetric(`${RENTER_METRIC_PREFIX} submitIdentityAnswers error`);
      }
      if (DatarouterError.is(error)) {
        switch (error.code) {
          case ErrorCodes.INCORRECT_ANSWERS: {
            // Extract remaining attempts from error details if available
            const remainingAttempts =
              typeof error.errorDetails === 'object' &&
              error.errorDetails !== null &&
              'remainingAttempts' in error.errorDetails
                ? ((error.errorDetails as { remainingAttempts?: number }).remainingAttempts ?? null)
                : null;
            setPidRemainingAttempts(remainingAttempts);
            onRequestStep(IdvStep.ErrorStep, INCORRECT_ANSWERS_SUBSTEP);
            break;
          }
          case ErrorCodes.USER_LOCKED_OUT:
            onRequestStep(IdvStep.ErrorStep, USER_LOCKED_OUT_SUBSTEP);
            break;
          default:
            log(LogLevel.Error, {
              message: `Unexpected error code for submitIdentityCheckAnswers: ${error.code}`,
              error,
            });
            onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
            break;
        }
      } else {
        log(LogLevel.Error, {
          message: 'Error calling submitIdentityCheckAnswers',
          error,
        });
        onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
      }
    },
    onSettled() {
      setLoading(false);
    },
  });

  return (
    <ReactFinalForm
      onSubmit={async (variables) => {
        const answers = Object.keys(variables)
          .sort()
          .map((key) => variables[key]);
        await mutateAsync({
          postData: {
            answers,
            deviceType: 'desktop',
            requestingClient,
            ssnProvided,
            zgAuthZone: getZgAuthZoneFromTermSet(termSet),
          },
        });
        return undefined;
      }}
      render={({ form, invalid }) => (
        <WizardStep
          id={id}
          hidden={kiqQuestions.length < 1}
          label={label}
          onSubmit={getWizardOnSubmit(form)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onSuccess={(_: any, abortController: AbortController) => {
            abortController.abort();
          }}
          onError={() => {
            // TODO - Should go to a generic error step
          }}
          onRequestNext={(abortController: AbortController) => {
            trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNext('id_verification_KBA_security_questions'));
            abortController.abort();
          }}
          meta={{
            renderNextButton: (buttonProps: ButtonProps) => (
              <Button {...buttonProps} tone="brand" emphasis="filled">
                Submit
              </Button>
            ),
            isNextDisabled: invalid,
            renderBackButton: () => null,
          }}
          render={() => {
            return (
              <Form onSubmit={onRequestNext}>
                <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}>
                  <Box css={{ display: 'flex', flexDirection: 'column', flexBasis: { lg: '60%' } }}>
                    <Heading level={2} textStyle="heading-sm">
                      Identity verification questions
                    </Heading>
                    {kiqQuestions.map((kiqQuestion, index) => (
                      <KiqQuestion question={kiqQuestion} index={index} key={kiqQuestion.questionText} />
                    ))}
                  </Box>
                  <Box css={{ display: 'flex', flexBasis: { lg: '40%' }, marginTop: { xlDown: 'loose' } }}>
                    <span>
                      <PrivacyCard />
                    </span>
                  </Box>
                </Box>
              </Form>
            );
          }}
        />
      )}
    />
  );
}
